// 계약 관리 — 설정 «알림»(C4b · 시스템 관리자): 채널 · 글 항목 · 미리보기 · 마지막 실행 · 저장.
// 숨김은 편의이고 막는 것은 서버다(require_contract_level("admin")). 미리보기는 보내지 않는다.
// 🔴 늦은 응답 버리기는 설정 화면의 useRequestGuards 와 같은 꼴이지만 import 하지 않는다(설정 화면 ↔ 이 패널 순환 방지)
//    — 그리고 요청 번호를 읽기용·미리보기용 둘로 따로 둔다(하나를 같이 쓰면 서로의 응답을 버린다).
// 🔴 글 항목 배열은 누른 순서가 아니라 응답 field_keys 순서다 · «바뀜» 비교는 집합 비교 · 0개도 허용.
import { useEffect, useRef, useState } from 'react';
import { contractsAPI } from '../../api/client';
import { alertDialog, toast } from '../../components/AppDialog';
import { COLORS, btn, card, input } from '../../utils/uiStyles';
import { NOTIFY_FIELD_LABELS, errorText, lastRunText } from './contractLabels';

const NOTICE = '이 채널의 모든 사람이 알림을 봅니다. 비공개 채널을 권합니다.';
const ALWAYS = '항상 실림 — 계약번호 · 기준일 · 남은 날';
const FALLBACK_KEYS = Object.keys(NOTIFY_FIELD_LABELS);
const muted = { color: COLORS.textMuted, fontSize: '12px' };
const heading = { fontSize: '12px', color: COLORS.textSub, fontWeight: 600, marginBottom: '6px' };
const offStyle = (style, off) => ({ ...style, opacity: off ? 0.5 : 1, cursor: off ? 'not-allowed' : 'pointer' });

// 표시 이름 = 사람이 넣은 이름(C4a2) · 비었으면 채널 id(C4a2 이전에 저장된 옛 행).
const displayName = (s) => String(s?.channel_name || s?.channel || '');
const keysOf = (s) => (Array.isArray(s?.field_keys) && s.field_keys.length > 0 ? s.field_keys : FALLBACK_KEYS);
const sameSet = (a, b) => {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const key of left) if (!right.has(key)) return false;
  return true;
};

// 읽기 실패(403 포함) — 설정 화면의 LoadError 와 같은 꼴(문구 + «다시 시도»).
function LoadError({ message, onRetry }) {
  return (
    <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
      <span>{message}</span>
      <button type="button" onClick={onRetry} style={btn('secondary', 'sm')}>다시 시도</button>
    </div>
  );
}

export default function ContractNotifyPanel() {
  const mountedRef = useRef(true);
  const loadSeqRef = useRef(0);      // 설정 읽기 번호
  const previewSeqRef = useRef(0);   // 미리보기 번호 — 읽기 번호와 따로
  const savingRef = useRef(false);
  const [settings, setSettings] = useState(null);   // 마지막으로 받은 응답(읽기·저장) — «바뀜» 비교의 기준
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [channel, setChannel] = useState('');
  const [picked, setPicked] = useState([]);          // 체크된 항목(field_keys 순)
  const [preview, setPreview] = useState(null);      // null = 만드는 중 · { text } · { failed: true }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadSeqRef.current += 1;
      previewSeqRef.current += 1;
    };
  }, []);

  const nextLoad = () => {
    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    return () => mountedRef.current && loadSeqRef.current === seq;
  };
  const nextPreview = () => {
    const seq = previewSeqRef.current + 1;
    previewSeqRef.current = seq;
    return () => mountedRef.current && previewSeqRef.current === seq;
  };

  const loadPreview = (fields) => {
    const isCurrent = nextPreview();
    setPreview(null);
    contractsAPI.previewNotify(fields)
      .then((r) => { if (isCurrent()) setPreview({ text: String(r.data?.text ?? '') }); })
      .catch(() => { if (isCurrent()) setPreview({ failed: true }); });
  };

  // 응답(읽기·저장)으로 화면을 채운다 — 입력칸 = 표시 이름(서버가 «~»·공백을 떼므로 다시 채워야 비교가 맞는다).
  const apply = (data) => {
    const fields = Array.isArray(data?.fields) ? data.fields : [];
    const chosen = keysOf(data).filter((key) => fields.includes(key));
    setSettings(data);
    setChannel(displayName(data));
    setPicked(chosen);
    return chosen;
  };

  const load = () => {
    const isCurrent = nextLoad();
    setLoading(true);
    contractsAPI.notifySettings()
      .then((r) => {
        if (!isCurrent()) return;
        const chosen = apply(r.data || {});
        setLoadError('');
        // 미리보기 첫 호출은 읽기가 성공한 뒤 — 받은 항목으로.
        loadPreview(chosen);
      })
      .catch(async (err) => {
        const message = await errorText(err, '알림 설정을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setSettings(null);
        setLoadError(message);
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const fieldKeys = keysOf(settings);
  const savedFields = Array.isArray(settings?.fields) ? settings.fields : [];
  const changed = !!settings && (channel.trim() !== displayName(settings) || !sameSet(picked, savedFields));

  const toggle = (key) => {
    if (savingRef.current) return;
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const ordered = fieldKeys.filter((k) => next.has(k));
    setPicked(ordered);
    loadPreview(ordered);
  };

  const save = async () => {
    if (savingRef.current || !changed) return;
    savingRef.current = true;
    setSaving(true);
    const body = { channel: channel.trim(), fields: fieldKeys.filter((k) => picked.includes(k)) };
    try {
      const r = await contractsAPI.saveNotifySettings(body);
      if (!mountedRef.current) return;
      const chosen = apply(r.data || {});
      toast('알림 설정을 저장했습니다.');
      if (!sameSet(chosen, body.fields)) loadPreview(chosen);
    } catch (err) {
      // 서버 400 문구(«봇이 이 채널에 없습니다…» 등)를 그대로 — 입력은 그대로 둔다.
      const message = await errorText(err, '알림 설정을 저장하지 못했습니다.');
      if (mountedRef.current) await alertDialog(message);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const saveOff = saving || !changed;
  return (
    <>
      {loading && <div style={{ ...muted, marginBottom: '8px' }}>불러오는 중…</div>}
      {loadError && <LoadError message={loadError} onRetry={load} />}
      {!loadError && settings && (
        <div style={{ ...card, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ padding: '8px 10px', borderRadius: '6px', background: COLORS.warnSoft, color: COLORS.warn, fontSize: '13px' }}>{NOTICE}</div>

          <div>
            <div data-testid="contract-notify-status" style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: settings.channel_set ? COLORS.text : COLORS.dangerText }}>
              {settings.channel_set ? `알림 채널: ${displayName(settings)}` : '알림 채널이 아직 지정되지 않았습니다 — 알림이 나가지 않습니다.'}
            </div>
            <input type="text" aria-label="알림 채널" value={channel} disabled={saving}
              placeholder="채널 URL 이름 또는 채널 id(비우면 알림 끔)"
              onChange={(e) => setChannel(e.target.value)}
              style={{ ...input, width: '360px', maxWidth: '100%' }} />
            <div style={{ ...muted, marginTop: '4px' }}>비공개 채널이면 먼저 봇을 채널에 초대해 주세요.</div>
          </div>

          <div>
            <div style={heading}>글 항목</div>
            <div style={{ ...muted, marginBottom: '6px' }}>{ALWAYS}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {fieldKeys.map((key) => (
                <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={picked.includes(key)} disabled={saving} onChange={() => toggle(key)} />
                  {NOTIFY_FIELD_LABELS[key] || key}
                </label>
              ))}
            </div>
          </div>

          <section aria-label="알림 미리보기">
            <div style={heading}>미리보기</div>
            {/* 🔴 role="status" 를 쓰지 않는다 — 설정 화면 시험의 idle() 이 status 가 사라지길 기다린다. */}
            {preview === null && <div style={muted}>미리보기를 만드는 중…</div>}
            {preview?.failed && <div style={{ fontSize: '12px', color: COLORS.dangerText }}>미리보기를 만들지 못했습니다.</div>}
            {preview && !preview.failed && (
              <pre style={{ margin: 0, padding: '10px 12px', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '6px', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{preview.text}</pre>
            )}
          </section>

          <div>
            <div style={heading}>마지막 실행</div>
            <div data-testid="contract-notify-last-run" style={{ fontSize: '13px', color: settings.last_run?.ok === false ? COLORS.dangerText : COLORS.text }}>
              {lastRunText(settings.last_run)}
            </div>
            {!settings.initialized && <div style={{ ...muted, marginTop: '4px' }}>첫 실행은 지난 기한을 보내지 않고 기록만 남깁니다.</div>}
          </div>

          <div>
            <button type="button" onClick={save} disabled={saveOff} aria-busy={saving || undefined}
              style={offStyle(btn('primary', 'sm'), saveOff)}>저장</button>
          </div>
        </div>
      )}
    </>
  );
}
